import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import './QRCodeModal.css';
import { getApiBaseUrl } from '../config/api';

const QRCodeModal = ({ device, onClose, onConnected }) => {
  const [qrCode, setQrCode] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchQRCode();
    const interval = setInterval(() => {
      checkConnectionStatus();
    }, 5000); // Increased from 2 seconds to 5 seconds

    return () => clearInterval(interval);
  }, [device]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQRCode = async () => {
    try {
      console.log('QRCodeModal: Fetching QR for device:', device);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${device.id}/qr`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      
      console.log('QR Response:', data); // Debug log
      
      if (data.success) {
        if (data.already_connected) {
          console.log('QRCodeModal: Device already connected');
          setStatus('Device is already fully connected and authenticated! ✅');
          setError('');
          // Remove auto-close, let user close manually
          onConnected(); // Just trigger refresh
        } else if (data.qr_code) {
          console.log('Setting QR code, length:', data.qr_code.length);
          setQrCode(data.qr_code);
          
          // Generate QR code image from text
          try {
            const qrImageUrl = await QRCode.toDataURL(data.qr_code, {
              width: 300,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF'
              }
            });
            setQrCodeImage(qrImageUrl);
            setStatus('Scan the QR code with your WhatsApp mobile app');
            setError('');
          } catch (qrError) {
            console.error('Failed to generate QR image:', qrError);
            setError('Failed to generate QR code image');
          }
        } else {
          console.log('No QR code in response:', data);
          setError('No QR code received from server. Please try again.');
        }
      } else {
        console.error('QR API error:', data.error);
        setError(data.error || 'Failed to get QR code');
      }
    } catch (error) {
      console.error('QR fetch error:', error);
      setError('Connection error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiBaseUrl(`/instances/${device.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      
      console.log('Connection status check:', data);
      
      if (data.success && data.instance) {
        console.log('Device status:', data.instance.status);
        if (data.instance.status === 'connected') {
          setStatus('Successfully connected and authenticated! ✅');
          // Remove auto-close, let user close manually
          onConnected(); // Just trigger refresh
        } else if (data.instance.status === 'connecting') {
          // Still connecting, keep QR code visible
          if (!error) { // Only update status if no error
            setStatus('Waiting for QR scan... Please scan the QR code with your WhatsApp mobile app');
          }
        } else {
          // Disconnected
          console.log('Device disconnected, status:', data.instance.status);
        }
      }
    } catch (error) {
      console.error('Status check failed:', error);
    }
  };

  const handleRefreshQR = () => {
    setLoading(true);
    setError('');
    setQrCode('');
    setQrCodeImage('');
    fetchQRCode();
  };

  return (
    <div className="qr-modal-overlay">
      <div className="qr-modal">
        <div className="qr-modal-header">
          <h3>Connect WhatsApp Device</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="qr-modal-content">
          <div className="device-info">
            <h4>{device.name}</h4>
            <p>{device.phone_number || 'Waiting for connection...'}</p>
          </div>

          {loading ? (
            <div className="qr-loading">
              <div className="loading-spinner"></div>
              <p>Generating QR Code...</p>
            </div>
          ) : error ? (
            <div className="qr-error">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button className="retry-btn" onClick={handleRefreshQR}>
                Try Again
              </button>
            </div>
          ) : qrCodeImage ? (
            <div className="qr-container">
              <div className="qr-code">
                <img src={qrCodeImage} alt="WhatsApp QR Code" />
              </div>
              <div className="qr-instructions">
                <h4>How to connect:</h4>
                <ol>
                  <li>Open WhatsApp on your phone</li>
                  <li>Tap Menu or Settings and select Linked Devices</li>
                  <li>Tap "Link a Device"</li>
                  <li>Point your phone at this screen to capture the code</li>
                </ol>
              </div>
            </div>
          ) : null}

          {status && (
            <div className="status-message">
              <p>{status}</p>
            </div>
          )}

          <div className="qr-actions">
            <button className="refresh-btn" onClick={handleRefreshQR}>
              🔄 Refresh QR Code
            </button>
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
